import React from 'react';
import { AlertTriangle, Building2, CalendarDays, CalendarPlus, CheckCircle, ChevronDown, LayoutDashboard, Landmark, LogOut, Menu, ShieldCheck, Users } from 'lucide-react';
import { ViewState, Language } from '../types';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface SidebarUser {
  id: string;
  name: string;
  role: 'cleaner' | 'supervisor' | 'admin';
  email: string;
  initials: string;
}

interface SidebarProps {
  currentView: ViewState;
  onChangeView: (view: ViewState) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  user: SidebarUser | null;
  onLogout: () => void;
  onEmergencyLogout: () => void;
}

const copy = {
  en: {
    dashboard: 'Dashboard',
    myJobs: 'My jobs',
    brfs: 'BRFs',
    calendar: 'General Calendar',
    extras: 'Extra blocks',
    team: 'Team',
    approvals: 'Approvals',
    qc: 'Quality Control',
    economy: 'Economy',
    notifications: 'Notifications',
    navigation: 'Navigation',
    resetLabel: 'Workspace',
    logout: 'Sign Out',
    emergencyLogout: 'Emergency Logout',
  },
  sv: {
    dashboard: 'Översikt',
    myJobs: 'Mina jobb',
    brfs: 'BRF:er',
    calendar: 'Generell kalender',
    extras: 'Extrastäd',
    team: 'Team',
    approvals: 'Godkänn',
    qc: 'Kvalitetskontroll',
    economy: 'Ekonomi',
    notifications: 'Notiser',
    navigation: 'Navigation',
    resetLabel: 'Arbetsyta',
    logout: 'Logga ut',
    emergencyLogout: 'Nödutloggning',
  },
} as const;

export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  onChangeView,
  language,
  setLanguage,
  user,
  onLogout,
  onEmergencyLogout,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const text = copy[language];

  const navItems = user?.role === 'cleaner'
    ? [
        { id: 'dashboard' as ViewState, label: text.myJobs, icon: LayoutDashboard },
      ]
    : user?.role === 'admin'
      ? [
          { id: 'dashboard' as ViewState, label: text.dashboard, icon: LayoutDashboard },
          { id: 'brfs' as ViewState, label: text.brfs, icon: Building2 },
          { id: 'calendar' as ViewState, label: text.calendar, icon: CalendarDays },
          { id: 'approvals' as ViewState, label: text.approvals, icon: CheckCircle },
          { id: 'qc' as ViewState, label: text.qc, icon: ShieldCheck },
          { id: 'economy' as ViewState, label: text.economy, icon: Landmark },
          { id: 'team' as ViewState, label: text.team, icon: Users },
        ]
    : [
        { id: 'dashboard' as ViewState, label: text.dashboard, icon: LayoutDashboard },
        { id: 'brfs' as ViewState, label: text.brfs, icon: Building2 },
        { id: 'calendar' as ViewState, label: text.calendar, icon: CalendarDays },
        { id: 'extras' as ViewState, label: text.extras, icon: CalendarPlus },
        { id: 'approvals' as ViewState, label: text.approvals, icon: CheckCircle },
        { id: 'qc' as ViewState, label: text.qc, icon: ShieldCheck },
      ];

  const navButton = (
    item: { id: ViewState; label: string; icon: typeof LayoutDashboard },
    isMobile = false,
  ) => {
    const Icon = item.icon;
    const active = currentView === item.id;

    return (
      <Button
        variant={active ? 'secondary' : 'ghost'}
        className={`w-full justify-start gap-3 ${
          active
            ? 'bg-sidebar-primary text-sidebar-primary-foreground font-semibold hover:bg-sidebar-primary hover:text-sidebar-primary-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
        } ${isMobile ? 'h-12 text-base' : 'h-10'}`}
        onClick={() => {
          onChangeView(item.id);
          setMobileMenuOpen(false);
        }}
      >
        <Icon size={isMobile ? 22 : 20} />
        <span>{item.label}</span>
      </Button>
    );
  };

  const languageToggle = (
    <div className="flex bg-sidebar-accent/30 rounded-lg p-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLanguage('sv')}
        className={`flex-1 h-8 text-xs font-medium ${
          language === 'sv'
            ? 'bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent'
            : 'text-sidebar-accent-foreground hover:bg-sidebar-accent/50'
        }`}
      >
        SV
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLanguage('en')}
        className={`flex-1 h-8 text-xs font-medium ${
          language === 'en'
            ? 'bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent'
            : 'text-sidebar-accent-foreground hover:bg-sidebar-accent/50'
        }`}
      >
        EN
      </Button>
    </div>
  );

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 h-auto py-3 px-3 bg-sidebar-accent/50 hover:bg-sidebar-accent border border-sidebar-border"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs">
              {user?.initials || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-sidebar-foreground truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-sidebar-accent-foreground truncate">
              {text.resetLabel}
            </p>
          </div>
          <ChevronDown className="h-4 w-4 text-sidebar-accent-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled className="opacity-100 focus:bg-transparent">
          <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{user?.role ?? 'cleaner'}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          {text.logout}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onEmergencyLogout}>
          <AlertTriangle className="mr-2 h-4 w-4" />
          {text.emergencyLogout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <div className="hidden md:flex w-64 bg-sidebar text-sidebar-foreground flex-col h-full shadow-xl border-r border-sidebar-border z-20 relative">
        <div className="p-4 border-b border-sidebar-border">
          <div className="bg-white rounded-lg p-2">
            <img src="/optimal_long.png" alt="Optimal Trappstädning" className="w-full h-auto" />
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
          <div className="space-y-1">
            <p className="px-2 text-xs font-semibold uppercase tracking-[0.2em] text-sidebar-accent-foreground">
              {text.navigation}
            </p>
            {navItems.map((item) => (
              <React.Fragment key={item.id}>{navButton(item)}</React.Fragment>
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-sidebar-border space-y-4">
          {languageToggle}
          {userMenu}
        </div>
      </div>

      <div className="md:hidden fixed top-0 left-0 right-0 bg-sidebar border-b border-sidebar-border z-50 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/optimal_long.png" alt="Optimal Trappstädning" className="h-8 w-auto" />
        </div>

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-sidebar-foreground">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 bg-sidebar text-sidebar-foreground border-sidebar-border">
            <SheetHeader>
              <SheetTitle className="text-sidebar-foreground">{text.navigation}</SheetTitle>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div className="space-y-2">
                {navItems.map((item) => (
                  <React.Fragment key={item.id}>{navButton(item, true)}</React.Fragment>
                ))}
              </div>
              {languageToggle}
              {userMenu}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
